import { GENERAL_CONFIG } from "../config/general.config"
import type {
    ProduktlyApi,
    ProduktlyConfigureParams,
    ProduktlyMetadata,
} from "../types/produktly.types"
// Ensure global Window augmentation is registered
import "../types/produktly.types"

const PRODUKTLY_CLIENT_TOKEN = import.meta.env.VITE_PRODUKTLY_CLIENT_TOKEN

export class ProduktlyHelper {
    private static didInit = false

    /**
     * Call once at app bootstrap when VITE_PRODUKTLY_CLIENT_TOKEN is set to a non-empty value.
     */
    static init(): void {
        if (this.didInit) {
            return
        }

        this.didInit = true

        if (!PRODUKTLY_CLIENT_TOKEN) {
            if (!GENERAL_CONFIG.isLocal) {
                console.warn(
                    "Produktly client token not set (VITE_PRODUKTLY_CLIENT_TOKEN). Produktly disabled.",
                )
            }

            return
        }

        try {
            this.installLoader(window, document, PRODUKTLY_CLIENT_TOKEN)
        } catch (error) {
            console.error("Failed to load Produktly script:", error)
        }
    }

    /**
     * SPA navigation for in-tour links (avoids full page reloads).
     */
    static configure(params: ProduktlyConfigureParams): void {
        if (!this.isEnabled()) {
            return
        }

        try {
            window.Produktly?.configure?.(params)
        } catch (error) {
            console.error("Produktly configure failed:", error)
        }
    }

    /**
     * Identify the user for dashboard segmentation (practice, role, flags, etc.).
     * Values are coerced to string | number per Produktly API.
     */
    static identifyUser(
        userId: string | number,
        metadata: Record<
            string,
            string | number | boolean | null | undefined
        > = {},
        options?: { hash?: string },
    ): void {
        if (!this.isEnabled()) {
            return
        }

        try {
            window.Produktly?.identifyUser?.(
                userId,
                this.normalizeMetadata(metadata),
                options,
            )
        } catch (error) {
            console.error("Produktly identifyUser failed:", error)
        }
    }

    /**
     * Check if Produktly is enabled
     */
    private static isEnabled(): boolean {
        return Boolean(PRODUKTLY_CLIENT_TOKEN)
    }

    /**
     * Loads Produktly asynchronously (same pattern as the dashboard snippet) so calls
     * queue until main.js is ready. No GTM required.
     * @see https://produktly.com/docs/docs/integration/examples
     */
    private static installLoader(
        w: Window,
        d: Document,
        clientToken: string,
    ): void {
        const win = w as Window & { ProduktlyQ?: unknown[] }

        win.Produktly =
            win.Produktly ||
            (new Proxy(
                function produktlyQueueTarget() {
                    /* target is never invoked; Produktly replaces window.Produktly after load */
                },
                {
                    get(_, prop) {
                        if (typeof prop !== "string") {
                            return undefined
                        }

                        return function (...args: unknown[]) {
                            win.ProduktlyQ = win.ProduktlyQ || []

                            win.ProduktlyQ.push([prop, args])
                        }
                    },
                    apply(_, __, argArray: unknown[]) {
                        win.ProduktlyQ = win.ProduktlyQ || []

                        win.ProduktlyQ.push(["apply", argArray])
                    },
                },
            ) as unknown as ProduktlyApi)

        if (d.getElementById(GENERAL_CONFIG.produktlyScriptId)) {
            return
        }

        const head = d.head

        const s = d.createElement("script")

        s.async = true

        s.src = GENERAL_CONFIG.produktlyScriptUrl

        s.id = GENERAL_CONFIG.produktlyScriptId

        s.dataset.clientToken = clientToken

        head.appendChild(s)
    }

    /**
     * Normalize metadata to ProduktlyMetadata type
     */
    private static normalizeMetadata(
        metadata: Record<string, string | number | boolean | null | undefined>,
    ): ProduktlyMetadata {
        const out: ProduktlyMetadata = {}

        for (const [key, value] of Object.entries(metadata)) {
            if (value === null || value === undefined) {
                continue
            }

            out[key] = typeof value === "boolean" ? Number(value) : value
        }

        return out
    }
}
